<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { ThemeConfig, ProfileThemeName } from '@/lib/profile-themes';
  import { resolveTheme, toStyleVars } from '@/lib/profile-themes';

  interface ShellProfile {
    display_name: string;
    username: string;
    avatar_initial: string;
    avatar_url?: string;
  }

  interface ShellTab {
    key: string;
    label: string;
    href: string;
    count?: number;
  }

  interface ShellTheme extends ThemeConfig {
    template?: ProfileThemeName;
  }

  let {
    profile,
    theme = { template: 'clean' },
    title,
    subtitle = '',
    backHref,
    backLabel = '프로필로 돌아가기',
    tabs = [],
    activeTab = null,
    width = '4xl',
    children,
  }: {
    profile: ShellProfile;
    theme?: ShellTheme;
    title: string;
    subtitle?: string;
    backHref: string;
    backLabel?: string;
    tabs?: ShellTab[];
    activeTab?: string | null;
    width?: '3xl' | '4xl' | '5xl';
    children?: Snippet;
  } = $props();

  const resolvedTheme = $derived(resolveTheme(theme.template || 'clean', theme));
  const styleVars = $derived(toStyleVars(resolvedTheme));
  const themeName = $derived(theme.template || 'clean');
  const needsGradientBg = $derived(['glass', 'ocean'].includes(themeName));
  const needsAuroraBg = $derived(themeName === 'aurora');
  const needsBackdrop = $derived(['glass', 'aurora', 'ocean'].includes(themeName));

  const widthClass = $derived(
    width === '5xl' ? 'max-w-5xl' : width === '3xl' ? 'max-w-3xl' : 'max-w-4xl'
  );
</script>

<div
  class="public-shell"
  class:theme-glass={needsGradientBg}
  class:theme-aurora={needsAuroraBg}
  class:needs-backdrop={needsBackdrop}
  style={styleVars}
>
  {#if needsAuroraBg}
    <div class="aurora-blobs">
      <div class="aurora-blob blob-1"></div>
      <div class="aurora-blob blob-2"></div>
      <div class="aurora-blob blob-3"></div>
    </div>
  {/if}

  <div class={`public-shell__container ${widthClass}`}>
    <div class="public-shell__header">
      <a href={backHref} class="public-shell__back-link">
        <svg class="public-shell__back-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        {backLabel}
      </a>

      <div class="public-shell__hero">
        {#if profile.avatar_url}
          <img
            src={profile.avatar_url}
            alt={profile.display_name}
            class="public-shell__avatar public-shell__avatar--image"
          />
        {:else}
          <div class="public-shell__avatar public-shell__avatar--fallback">
            {profile.avatar_initial}
          </div>
        {/if}

        <div class="public-shell__hero-copy">
          <p class="public-shell__eyebrow">@{profile.username}</p>
          <h1 class="public-shell__title">{title}</h1>
          {#if subtitle}
            <p class="public-shell__subtitle">{subtitle}</p>
          {/if}
        </div>
      </div>

      {#if tabs.length > 0}
        <div class="public-shell__tabs" role="tablist" aria-label="공개 프로필 섹션">
          {#each tabs as tab}
            <a
              href={tab.href}
              class:public-shell__tab--active={activeTab === tab.key}
              class="public-shell__tab"
              aria-current={activeTab === tab.key ? 'page' : undefined}
            >
              <span>{tab.label}</span>
              {#if tab.count !== undefined}
                <span class="public-shell__tab-count">{tab.count}</span>
              {/if}
            </a>
          {/each}
        </div>
      {/if}
    </div>

    <div class="public-shell__content">
      {@render children?.()}
    </div>
  </div>
</div>

<style>
  .public-shell {
    min-height: 100vh;
    background: var(--page-bg);
    color: var(--text-primary, #1f2937);
    font-family: var(--font-body, 'SUIT Variable', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', system-ui, sans-serif);
    padding: 40px 16px 72px;
    position: relative;
    overflow-x: hidden;
  }

  .public-shell__container {
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .theme-glass {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .theme-aurora {
    background: #0f0c29;
  }

  .aurora-blobs {
    position: fixed;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .aurora-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.5;
    animation: aurora-float 12s ease-in-out infinite alternate;
  }

  .blob-1 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, #ff6b6b 0%, transparent 70%);
    top: -100px;
    right: -50px;
    animation-delay: 0s;
  }

  .blob-2 {
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, #4ecdc4 0%, transparent 70%);
    bottom: 20%;
    left: -80px;
    animation-delay: -4s;
  }

  .blob-3 {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, #45b7d1 0%, transparent 70%);
    top: 40%;
    right: -40px;
    animation-delay: -8s;
  }

  @keyframes aurora-float {
    0% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -20px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
    100% { transform: translate(10px, -10px) scale(1.05); }
  }

  .public-shell__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 24px;
  }

  .public-shell__back-link {
    width: fit-content;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--text-secondary, #6b7280);
    text-decoration: none;
    transition: color 150ms ease;
  }

  .public-shell__back-link:hover {
    color: var(--text-primary, #111827);
  }

  .public-shell__back-icon {
    width: 16px;
    height: 16px;
  }

  .public-shell__hero {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    background: var(--card-bg, rgba(255, 255, 255, 0.92));
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    border-radius: calc(var(--card-radius, 18px) + 6px);
    box-shadow: var(--card-shadow, 0 12px 32px rgba(15, 23, 42, 0.08));
  }

  .public-shell__avatar {
    width: 56px;
    height: 56px;
    border-radius: var(--avatar-radius, 9999px);
    border: 1px solid var(--avatar-border, transparent);
    flex-shrink: 0;
  }

  .public-shell__avatar--image {
    object-fit: cover;
  }

  .public-shell__avatar--fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--avatar-bg, #111827);
    color: var(--avatar-text, #ffffff);
    font-weight: 700;
    font-size: 18px;
  }

  .public-shell__hero-copy {
    min-width: 0;
  }

  .public-shell__eyebrow {
    margin: 0 0 4px;
    color: var(--text-secondary, #6b7280);
    font-size: 13px;
    font-weight: 600;
  }

  .public-shell__title {
    margin: 0;
    color: var(--text-primary, #111827);
    font-family: var(--font-heading, 'Outfit', 'SUIT Variable', sans-serif);
    font-size: clamp(1.5rem, 3vw, 2.1rem);
    line-height: 1.1;
  }

  .public-shell__subtitle {
    margin: 8px 0 0;
    color: var(--text-secondary, #6b7280);
    font-size: 14px;
  }

  .public-shell__tabs {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .public-shell__tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 9999px;
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    background: var(--card-bg, rgba(255, 255, 255, 0.8));
    color: var(--text-secondary, #6b7280);
    text-decoration: none;
    transition: transform 150ms ease, border-color 150ms ease, color 150ms ease, background 150ms ease;
  }

  .public-shell__tab:hover {
    transform: translateY(-1px);
    color: var(--text-primary, #111827);
    border-color: color-mix(in srgb, var(--accent, #111827) 28%, transparent);
  }

  .public-shell__tab--active {
    color: var(--button-text, #ffffff);
    background: var(--button-bg, var(--accent, #111827));
    border-color: var(--button-border, var(--accent, #111827));
    box-shadow: var(--card-shadow, 0 10px 24px rgba(15, 23, 42, 0.12));
  }

  .public-shell__tab-count {
    color: inherit;
    opacity: 0.72;
  }

  .public-shell__content {
    color: var(--text-primary, #111827);
  }

  :global(.public-subpage-card) {
    background: var(--card-bg, rgba(255, 255, 255, 0.92));
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    border-radius: var(--card-radius, 18px);
    box-shadow: var(--card-shadow, 0 10px 24px rgba(15, 23, 42, 0.08));
  }

  :global(.public-subpage-card:hover) {
    background: var(--card-hover, var(--card-bg, rgba(255, 255, 255, 0.96)));
    border-color: color-mix(in srgb, var(--accent, #111827) 24%, transparent);
  }

  .needs-backdrop .public-shell__hero,
  .needs-backdrop .public-shell__tab,
  :global(.needs-backdrop .public-subpage-card),
  :global(.needs-backdrop .public-pagination-button) {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  :global(.public-subpage-muted) {
    color: var(--text-secondary, #6b7280);
  }

  :global(.public-subpage-faint) {
    color: var(--text-tertiary, #9ca3af);
  }

  :global(.public-subpage-accent) {
    color: var(--accent, #111827);
  }

  :global(.public-subpage-pill) {
    background: color-mix(in srgb, var(--accent, #111827) 10%, transparent);
    color: var(--accent, #111827);
    border: 1px solid color-mix(in srgb, var(--accent, #111827) 16%, transparent);
    border-radius: 9999px;
  }

  @media (max-width: 640px) {
    .public-shell {
      padding-top: 28px;
      padding-bottom: 40px;
    }

    .public-shell__hero {
      padding: 18px;
      align-items: flex-start;
    }

    .public-shell__avatar {
      width: 48px;
      height: 48px;
    }
  }
</style>
